import React, { useEffect, useRef, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import styles from "./Header.module.css";
import { AiDmsLogo, DefaultAvatar } from "../../assets/images";
import { useLanguage } from "../../contexts/language/LanguageContext";
import SettingModal from "../SettingModal/SettingModal";
import {
  fetchUiSettings,
  getAccessToken,
  getStoredAuthUser,
  logout,
} from "../../services/auth";

const Header = () => {
  const { t, lang, switchLanguage } = useLanguage();
  const navigate = useNavigate();

  // --- STATE ---
  const [showDropdown, setShowDropdown] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false); // NEW: State for mobile menu
  const dropdownRef = useRef(null);

  const [openSetting, setOpenSetting] = useState(false);
  const [authUser, setAuthUser] = useState(() => getStoredAuthUser());

  const user = {
    name: authUser?.username || "Admin User",
    email: authUser?.email || "",
    avatar: DefaultAvatar,
  };

  const navItems = [
    { label: t("header_nav_scan"), href: "/" },
    { label: t("header_nav_templates"), href: "/templates" },
    { label: t("header_nav_naming_rules"), href: "/naming-rules" },
  ];

  // Note: Mobile navigation often requires different active styles (e.g., block background)
  // We handle this in CSS now using the .activeLink class instead of inline styles for better responsiveness.

  // --- EFFECTS ---
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const token = getAccessToken();
    if (!token) return;

    let cancelled = false;

    fetchUiSettings(token)
      .then((data) => {
        if (!cancelled && data?.user) {
          setAuthUser(data.user);
        }
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, []);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  // NEW: Close mobile menu when a link is clicked
  const onNavClick = () => {
    setMobileMenuOpen(false);
  };

  const handleSettingSaved = () => {
    window.location.reload();
  };

  return (
    <>
      <header className={styles.header}>
        {/* --- 1. HAMBURGER BTN (Visible only on Mobile) --- */}
        <button
          className={styles.hamburgerBtn}
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        >
          {/* Simple SVG Icon for Menu */}
          <svg
            viewBox="0 0 24 24"
            width="24"
            height="24"
            stroke="currentColor"
            strokeWidth="2"
            fill="none"
          >
            <line x1="3" y1="12" x2="21" y2="12"></line>
            <line x1="3" y1="6" x2="21" y2="6"></line>
            <line x1="3" y1="18" x2="21" y2="18"></line>
          </svg>
        </button>

        {/* --- 2. LOGO --- */}
        <div className={styles.logoWrapper}>
          <div className={styles.logoPlaceholder} onClick={() => navigate("/")}>
            <img src={AiDmsLogo} alt="AI DMS Logo" />
          </div>
        </div>

        {/* --- 3. DESKTOP NAVIGATION (Hidden on Mobile) --- */}
        <nav className={`${styles.nav} ${styles.desktopNav}`}>
          <ul className={styles.navList}>
            {navItems.map((item, index) => (
              <li key={index} className={styles.navItem}>
                <NavLink
                  to={item.href}
                  className={({ isActive }) =>
                    `${styles.navLink} ${isActive ? styles.activeLink : ""}`
                  }
                >
                  {item.label}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        {/* --- 4. RIGHT SECTION --- */}
        <div className={styles.rightSection}>
          <div className={styles.langSwitcher}>
            <button
              className={`${styles.langBtn} ${lang === "vi" ? styles.activeLang : ""}`}
              onClick={() => switchLanguage("vi")}
            >
              VN
            </button>
            <button
              className={`${styles.langBtn} ${lang === "en" ? styles.activeLang : ""}`}
              onClick={() => switchLanguage("en")}
            >
              EN
            </button>
          </div>
          <div className={styles.separator}></div>

          <div className={styles.userWrapper} ref={dropdownRef}>
            <div
              className={styles.avatarBox}
              onClick={() => setShowDropdown(!showDropdown)}
            >
              <img
                src={user.avatar}
                alt="Avatar"
                className={styles.avatarImg}
              />
              <span className={styles.userName}>{user.name}</span>
            </div>

            {showDropdown && (
              <div className={styles.dropdownMenu}>
                <div className={styles.dropdownHeader}>
                  <strong>{user.name}</strong>
                  {user.email && <span>{user.email}</span>}
                </div>
                <ul className={styles.dropdownList}>
                  <li
                    onClick={() => {
                      setOpenSetting(true);
                      setShowDropdown(false);
                    }}
                  >
                    {t("header_setting")}
                  </li>
                  <li>{t("header_profile")}</li>
                  <li className={styles.logoutItem} onClick={handleLogout}>
                    {t("header_logout")}
                  </li>
                </ul>
              </div>
            )}
          </div>
        </div>

        {/* --- 5. MOBILE MENU OVERLAY (New) --- */}
        {/* Renders a slide-down menu when mobileMenuOpen is true */}
        <div
          className={`${styles.mobileMenu} ${mobileMenuOpen ? styles.mobileMenuOpen : ""}`}
        >
          <ul className={styles.mobileNavList}>
            {navItems.map((item, index) => (
              <li key={index}>
                <NavLink
                  to={item.href}
                  className={({ isActive }) =>
                    `${styles.mobileNavLink} ${isActive ? styles.activeLink : ""}`
                  }
                  onClick={onNavClick}
                >
                  {item.label}
                </NavLink>
              </li>
            ))}
          </ul>
        </div>
      </header>

      <SettingModal
        isOpen={openSetting}
        onClose={() => setOpenSetting(false)}
        onSaved={handleSettingSaved}
      />
    </>
  );
};

export default Header;
